"use client";

import {
  ATSJobStatus,
  atsResumeService,
} from "@/_services/resumeServices/ats-resume";
import { resumeBenchmarkService } from "@/_services/resumeServices/benchmark";
import { useAuthStore } from "@/_stores/useAuthStore";
import { getMessageFromAxiosError } from "@/_utils";
import { toaster } from "@/_components/lib/ui/toaster";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { useTranslations } from "next-intl";
import { downloadResumePdf } from "./resumeDownload";

const POLLING_INTERVAL_MS = 3000;
const PROGRESS_TICK_MS = 350;
const PROGRESS_STEP = 2.5;
const MAX_PROCESSING_PROGRESS = 90;
const ATS_I18N_NS =
  "resume_community.talent_pool.resume_detail.resume.ats_generate.dialog";

interface UseGenerateATSResumeOptions {
  resumeId?: string;
  historyId?: string;
  fileBasename: string;
  /** When provided, called with the generated file URL instead of auto-downloading */
  onSuccess?: (fileUrl: string) => void;
}

const getActiveStepIndex = (
  progress: number,
  jobStatus: ATSJobStatus | null,
): number => {
  if (jobStatus === ATSJobStatus.COMPLETED) return 2;
  if (progress >= 72) return 2;
  if (progress >= 36) return 1;
  return 0;
};

export const useGenerateATSResume = ({
  resumeId,
  historyId,
  fileBasename,
  onSuccess,
}: UseGenerateATSResumeOptions) => {
  const t = useTranslations(ATS_I18N_NS);
  const token = useAuthStore((s) => s.token);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<ATSJobStatus | null>(null);
  const [progress, setProgress] = useState(8);
  const hasDownloadedRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const activeStepIndex = useMemo(
    () => getActiveStepIndex(progress, jobStatus),
    [jobStatus, progress],
  );

  const shouldAnimateProgress =
    isDialogOpen &&
    jobStatus !== ATSJobStatus.COMPLETED &&
    jobStatus !== ATSJobStatus.FAILED;

  const resetFlow = useCallback(() => {
    setJobId(null);
    setJobStatus(null);
    setProgress(8);
    hasDownloadedRef.current = false;
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    resetFlow();
  }, [resetFlow]);

  const generateMutationKey = historyId
    ? `/resume-benchmark/history/${historyId}/ats-generate`
    : `/ats/generate/${resumeId}`;

  const { trigger: generateATS, isMutating: isGenerating } = useSWRMutation(
    token && generateMutationKey ? generateMutationKey : null,
    () =>
      historyId
        ? resumeBenchmarkService.generateATSFromHistory(historyId)
        : atsResumeService.generateATS(resumeId!),
  );

  useEffect(() => {
    if (!shouldAnimateProgress) return;

    const intervalId = window.setInterval(() => {
      setProgress((prev) =>
        Math.min(prev + PROGRESS_STEP, MAX_PROCESSING_PROGRESS),
      );
    }, PROGRESS_TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [shouldAnimateProgress]);

  const { data: statusData } = useSWR(
    token && jobId && isDialogOpen ? `/ats/${jobId}/status` : null,
    () => atsResumeService.checkStatusATS(jobId!),
    {
      revalidateOnFocus: false,
      refreshInterval(latestData) {
        if (
          latestData?.status === ATSJobStatus.COMPLETED ||
          latestData?.status === ATSJobStatus.FAILED
        ) {
          return 0;
        }
        return POLLING_INTERVAL_MS;
      },
    },
  );

  useEffect(() => {
    if (!statusData) return;

    setJobStatus(statusData.status);

    if (statusData.status === ATSJobStatus.FAILED) {
      toaster.error({
        title: t("toast_generate_failed"),
      });
      return;
    }

    if (
      statusData.status === ATSJobStatus.COMPLETED &&
      statusData.file_url &&
      !hasDownloadedRef.current
    ) {
      hasDownloadedRef.current = true;
      setProgress(100);

      if (onSuccessRef.current) {
        onSuccessRef.current(statusData.file_url);
        closeDialog();
      } else {
        void downloadResumePdf(
          statusData.file_url,
          `${fileBasename}-ats`,
        ).finally(() => {
          closeDialog();
        });
      }
    }
  }, [closeDialog, fileBasename, statusData, t]);

  const startGenerate = useCallback(async () => {
    resetFlow();
    setIsDialogOpen(true);
    setJobStatus(ATSJobStatus.PROCESSING);

    try {
      const result = await generateATS();
      setJobId(result.jobId);
      setJobStatus(result.status);
    } catch (error) {
      setJobStatus(ATSJobStatus.FAILED);
      toaster.error({
        title: t("toast_start_failed"),
        description: getMessageFromAxiosError(error) || undefined,
      });
    }
  }, [generateATS, resetFlow, t]);

  return {
    isDialogOpen,
    jobStatus,
    progress,
    activeStepIndex,
    isGenerating,
    startGenerate,
    closeDialog,
    retryGenerate: startGenerate,
  };
};
